<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import {
  cortexKnowledgeBasesAPI,
  cortexFirecrawlConfigurationAPI,
  cortexUrlCandidatesAPI,
  cortexUrlDomainPoliciesAPI,
  cortexUrlPolicyAPI,
} from 'dashboard/api/cortex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextCheckbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import SectionLayout from '../../settings/account/components/SectionLayout.vue';

const props = defineProps({
  firecrawlOnly: { type: Boolean, default: false },
});
const { t, locale } = useI18n();
const isLoading = ref(false);
const isSavingPolicy = ref(false);
const isSavingFirecrawl = ref(false);
const isTestingFirecrawl = ref(false);
const isSavingDomain = ref(false);
const activeCandidateAction = ref('');
const activeDomainAction = ref('');
const candidates = ref([]);
const domainPolicies = ref([]);
const knowledgeBases = ref([]);
const candidateKnowledgeBases = reactive({});
const domainForm = reactive({ domain: '', status: 'allowed' });
const policy = reactive({
  conversation_url_extraction_mode: 'review_required',
  max_links_per_message: 3,
  max_links_per_conversation: 10,
  max_links_per_period: 100,
  domain_approval_required: true,
  process_youtube: true,
  allow_youtube_audio: false,
  model_fallback_enabled: true,
  model_fallback_budget_usd: 0,
  canonical_locale: 'pt_BR',
  settings: { max_concurrent_ingestions: 2 },
});
const firecrawl = reactive({
  mode: 'platform',
  endpoint: '',
  api_key: '',
  available: false,
  source: 'default',
  effective_mode: 'internal',
  health: {},
});

const normalizedRecords = response =>
  response.data?.payload || response.data || [];
const modeOptions = computed(() => [
  { value: 'disabled', label: t('CORTEX_URLS.POLICY.MODES.disabled') },
  {
    value: 'review_required',
    label: t('CORTEX_URLS.POLICY.MODES.review_required'),
  },
  { value: 'automatic', label: t('CORTEX_URLS.POLICY.MODES.automatic') },
]);
const firecrawlModeOptions = computed(() => [
  { value: 'platform', label: t('CORTEX_URLS.FIRECRAWL.MODES.platform') },
  { value: 'cloud', label: t('CORTEX_URLS.FIRECRAWL.MODES.cloud') },
  { value: 'custom', label: t('CORTEX_URLS.FIRECRAWL.MODES.custom') },
  { value: 'disabled', label: t('CORTEX_URLS.FIRECRAWL.MODES.disabled') },
]);
const firecrawlSourceBadge = computed(() =>
  firecrawl.source === 'account'
    ? t('CORTEX_MODELS.CONNECTIONS.ACCOUNT')
    : t('CORTEX_MODELS.CONNECTIONS.PLATFORM')
);
const firecrawlSourceBadgeColor = computed(() =>
  firecrawl.source === 'account' ? 'slate' : 'brand'
);
const firecrawlEffectiveModeLabel = computed(() =>
  t(`CORTEX_MODELS.CONNECTIONS.ORIGIN_MODES.${firecrawl.effective_mode}`)
);
const firecrawlHealthLabel = computed(() => {
  const labels = {
    online: t('CORTEX_MODELS.CONNECTIONS.HEALTH.ONLINE'),
    authentication_failed: t(
      'CORTEX_MODELS.CONNECTIONS.HEALTH.AUTHENTICATION_FAILED'
    ),
    under_pressure: t('CORTEX_MODELS.CONNECTIONS.HEALTH.UNDER_PRESSURE'),
    quota_exhausted: t('CORTEX_MODELS.CONNECTIONS.HEALTH.QUOTA_EXHAUSTED'),
    offline: t('CORTEX_MODELS.CONNECTIONS.HEALTH.OFFLINE'),
    incompatible: t('CORTEX_MODELS.CONNECTIONS.HEALTH.INCOMPATIBLE'),
    unconfigured: t('CORTEX_MODELS.CONNECTIONS.HEALTH.UNCONFIGURED'),
    disabled: t('CORTEX_URLS.FIRECRAWL.HEALTH.DISABLED'),
  };

  return firecrawl.health.status
    ? labels[firecrawl.health.status] ||
        t('CORTEX_MODELS.CONNECTIONS.HEALTH.UNKNOWN')
    : t('CORTEX_MODELS.CONNECTIONS.HEALTH.NOT_TESTED');
});
const firecrawlHealthBadgeLabel = computed(() => {
  if (firecrawl.health.status === 'online') {
    return t('CORTEX_MODELS.CONNECTIONS.HEALTH.OPERATIONAL');
  }

  return firecrawl.health.status
    ? t('CORTEX_MODELS.CONNECTIONS.HEALTH.NOT_OPERATIONAL')
    : t('CORTEX_MODELS.CONNECTIONS.HEALTH.NOT_TESTED');
});
const firecrawlHealthBadgeColor = computed(() => {
  if (firecrawl.health.status === 'online') return 'teal';

  return firecrawl.health.status ? 'ruby' : 'slate';
});
const localeOptions = computed(() => [
  {
    value: 'pt_BR',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.pt_BR'
    ),
  },
  {
    value: 'pt',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.pt'
    ),
  },
  {
    value: 'en',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.en'
    ),
  },
  {
    value: 'es',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.es'
    ),
  },
  {
    value: 'de',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.de'
    ),
  },
  {
    value: 'fr',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.fr'
    ),
  },
  {
    value: 'it',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.it'
    ),
  },
]);
const domainStatusOptions = computed(() => [
  { value: 'allowed', label: t('CORTEX_URLS.DOMAINS.STATUS.allowed') },
  { value: 'blocked', label: t('CORTEX_URLS.DOMAINS.STATUS.blocked') },
]);
const policyBooleanFields = computed(() => [
  {
    key: 'domain_approval_required',
    label: t('CORTEX_URLS.POLICY.FIELDS.DOMAIN_APPROVAL_REQUIRED'),
    help: t('CORTEX_URLS.POLICY.HELP.DOMAIN_APPROVAL_REQUIRED'),
  },
  {
    key: 'process_youtube',
    label: t('CORTEX_URLS.POLICY.FIELDS.PROCESS_YOUTUBE'),
    help: t('CORTEX_URLS.POLICY.HELP.PROCESS_YOUTUBE'),
  },
  {
    key: 'allow_youtube_audio',
    label: t('CORTEX_URLS.POLICY.FIELDS.ALLOW_YOUTUBE_AUDIO'),
    help: t('CORTEX_URLS.POLICY.HELP.ALLOW_YOUTUBE_AUDIO'),
  },
  {
    key: 'model_fallback_enabled',
    label: t('CORTEX_URLS.POLICY.FIELDS.MODEL_FALLBACK_ENABLED'),
    help: t('CORTEX_URLS.POLICY.HELP.MODEL_FALLBACK_ENABLED'),
  },
]);
const domainStatusLabel = status =>
  domainStatusOptions.value.find(option => option.value === status)?.label ||
  status;
const domainStatusColor = status => (status === 'allowed' ? 'teal' : 'ruby');
const candidateStatusLabel = status =>
  ({
    pending: t('CORTEX_URLS.STATUS.pending'),
    active: t('CORTEX_URLS.STATUS.active'),
    syncing: t('CORTEX_URLS.STATUS.syncing'),
    failed: t('CORTEX_URLS.STATUS.failed'),
    archived: t('CORTEX_URLS.STATUS.archived'),
  })[status] || status;
const candidateStatusColor = status =>
  ({
    pending: 'amber',
    active: 'teal',
    syncing: 'brand',
    failed: 'ruby',
    archived: 'slate',
  })[status] || 'slate';
const knowledgeBaseOptions = computed(() =>
  knowledgeBases.value.map(base => ({ value: base.id, label: base.name }))
);
const reviewableCandidates = computed(() =>
  candidates.value.filter(candidate =>
    ['pending', 'failed'].includes(candidate.status)
  )
);

const fetchData = async () => {
  isLoading.value = true;
  try {
    if (props.firecrawlOnly) {
      const { data } = await cortexFirecrawlConfigurationAPI.get();
      Object.assign(firecrawl, data, { api_key: '' });
      return true;
    }

    const [
      policyResponse,
      candidatesResponse,
      domainsResponse,
      basesResponse,
      firecrawlResponse,
    ] = await Promise.all([
      cortexUrlPolicyAPI.get(),
      cortexUrlCandidatesAPI.list({ per_page: 100 }),
      cortexUrlDomainPoliciesAPI.list({ per_page: 100 }),
      cortexKnowledgeBasesAPI.get(),
      cortexFirecrawlConfigurationAPI.get(),
    ]);
    Object.assign(firecrawl, firecrawlResponse.data, { api_key: '' });
    Object.assign(policy, policyResponse.data);
    candidates.value = normalizedRecords(candidatesResponse);
    domainPolicies.value = normalizedRecords(domainsResponse);
    knowledgeBases.value = normalizedRecords(basesResponse).filter(
      base => base.status === 'active'
    );
    reviewableCandidates.value.forEach(candidate => {
      candidateKnowledgeBases[candidate.id] =
        candidate.cortex_knowledge_base_id ||
        knowledgeBases.value[0]?.id ||
        null;
    });
    return true;
  } catch (error) {
    useAlert(t('CORTEX_URLS.API.ERROR'));
    return false;
  } finally {
    isLoading.value = false;
  }
};

const saveFirecrawl = async () => {
  isSavingFirecrawl.value = true;
  try {
    const { data } = await cortexFirecrawlConfigurationAPI.update({
      ...firecrawl,
    });
    Object.assign(firecrawl, data, { api_key: '' });
    useAlert(t('CORTEX_URLS.FIRECRAWL.SAVED'));
  } catch (error) {
    useAlert(error.response?.data?.errors?.[0] || t('CORTEX_URLS.API.ERROR'));
  } finally {
    isSavingFirecrawl.value = false;
  }
};

const testFirecrawl = async ({ notify = true } = {}) => {
  if (isTestingFirecrawl.value) return;

  isTestingFirecrawl.value = true;
  try {
    const { data } = await cortexFirecrawlConfigurationAPI.test();
    Object.assign(firecrawl, {
      available: data.status === 'online',
      health: data,
      source: data.source,
      effective_mode: data.mode,
    });
    if (notify) {
      useAlert(
        data.status === 'online'
          ? t('CORTEX_MODELS.CONNECTIONS.TEST_SUCCESS')
          : t('CORTEX_MODELS.CONNECTIONS.TEST_FAILED')
      );
    }
  } catch (error) {
    if (notify) useAlert(t('CORTEX_MODELS.CONNECTIONS.TEST_FAILED'));
  } finally {
    isTestingFirecrawl.value = false;
  }
};

const savePolicy = async () => {
  isSavingPolicy.value = true;
  try {
    const { data } = await cortexUrlPolicyAPI.update({
      ...policy,
      max_links_per_message: Number(policy.max_links_per_message),
      max_links_per_conversation: Number(policy.max_links_per_conversation),
      max_links_per_period: Number(policy.max_links_per_period),
      model_fallback_budget_usd: Number(policy.model_fallback_budget_usd),
      settings: {
        ...policy.settings,
        max_concurrent_ingestions: Number(
          policy.settings.max_concurrent_ingestions
        ),
      },
    });
    Object.assign(policy, data);
    useAlert(t('CORTEX_URLS.POLICY.SAVED'));
  } catch (error) {
    useAlert(t('CORTEX_URLS.API.ERROR'));
  } finally {
    isSavingPolicy.value = false;
  }
};

const addDomain = async () => {
  isSavingDomain.value = true;
  try {
    await cortexUrlDomainPoliciesAPI.create(domainForm);
    domainForm.domain = '';
    useAlert(t('CORTEX_URLS.DOMAINS.SAVED'));
    await fetchData();
  } catch (error) {
    useAlert(t('CORTEX_URLS.API.ERROR'));
  } finally {
    isSavingDomain.value = false;
  }
};

const updateDomain = async (domainPolicy, status) => {
  activeDomainAction.value = `${domainPolicy.id}:${status}`;
  try {
    await cortexUrlDomainPoliciesAPI.update(domainPolicy.id, { status });
    await fetchData();
  } catch (error) {
    useAlert(t('CORTEX_URLS.API.ERROR'));
  } finally {
    activeDomainAction.value = '';
  }
};

const deleteDomain = async domainPolicy => {
  activeDomainAction.value = `${domainPolicy.id}:delete`;
  try {
    await cortexUrlDomainPoliciesAPI.delete(domainPolicy.id);
    await fetchData();
  } catch (error) {
    useAlert(t('CORTEX_URLS.API.ERROR'));
  } finally {
    activeDomainAction.value = '';
  }
};

const decideCandidate = async (candidate, decision) => {
  activeCandidateAction.value = `${candidate.id}:${decision}`;
  try {
    await cortexUrlCandidatesAPI.decide(candidate.id, {
      decision,
      knowledge_base_id:
        decision === 'ignore'
          ? undefined
          : candidateKnowledgeBases[candidate.id],
    });
    useAlert(t('CORTEX_URLS.CANDIDATES.DECIDED'));
    await fetchData();
  } catch (error) {
    useAlert(t('CORTEX_URLS.API.ERROR'));
  } finally {
    activeCandidateAction.value = '';
  }
};

const formatDate = value =>
  new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(new Date(value));

onMounted(async () => {
  const loaded = await fetchData();
  if (loaded && props.firecrawlOnly) await testFirecrawl({ notify: false });
});
</script>

<template>
  <SectionLayout
    :title="
      firecrawlOnly ? t('CORTEX_URLS.FIRECRAWL.TITLE') : t('CORTEX_URLS.TITLE')
    "
    :description="
      firecrawlOnly
        ? t('CORTEX_URLS.FIRECRAWL.DESCRIPTION')
        : t('CORTEX_URLS.DESCRIPTION')
    "
    with-border
  >
    <woot-loading-state v-if="isLoading" />
    <div v-else class="grid gap-6">
      <form
        v-if="firecrawlOnly"
        class="grid gap-4 rounded-lg border border-n-weak bg-n-alpha-1 p-4"
        @submit.prevent="saveFirecrawl"
      >
        <div class="grid gap-4 md:grid-cols-2">
          <WithLabel
            name="cortex-firecrawl-mode"
            :label="t('CORTEX_URLS.FIRECRAWL.MODE')"
            :help-message="t('CORTEX_URLS.FIRECRAWL.MODE_HELP')"
          >
            <AdaptiveSelect
              v-model="firecrawl.mode"
              :options="firecrawlModeOptions"
            />
          </WithLabel>
          <div
            class="grid content-center gap-1 text-label-small leading-4 text-n-slate-11"
          >
            <div class="flex flex-wrap items-center gap-2">
              <Label
                :label="firecrawlSourceBadge"
                :color="firecrawlSourceBadgeColor"
                compact
              />
              <Label
                :label="firecrawlEffectiveModeLabel"
                color="slate"
                compact
              />
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <Label
                :label="firecrawlHealthBadgeLabel"
                :color="firecrawlHealthBadgeColor"
                compact
              />
              <span
                v-if="
                  firecrawl.health.status &&
                  firecrawl.health.status !== 'online'
                "
              >
                {{ firecrawlHealthLabel }}
              </span>
              <template
                v-if="
                  firecrawl.health.latency_ms !== null &&
                  firecrawl.health.latency_ms !== undefined
                "
              >
                {{ t('CORTEX_MODELS.SEPARATOR') }}
                {{
                  t('CORTEX_URLS.FIRECRAWL.HEALTH.LATENCY', {
                    latency: firecrawl.health.latency_ms,
                  })
                }}
              </template>
            </div>
            <time
              v-if="firecrawl.health.checked_at"
              :datetime="firecrawl.health.checked_at"
              data-testid="firecrawl-checked-at"
              class="text-xs text-n-slate-10"
            >
              {{
                t('CORTEX_MODELS.CONNECTIONS.HEALTH.LAST_CHECKED', {
                  date: formatDate(firecrawl.health.checked_at),
                })
              }}
            </time>
          </div>
          <NextInput
            v-if="firecrawl.mode === 'custom'"
            v-model="firecrawl.endpoint"
            type="url"
            :label="t('CORTEX_URLS.FIRECRAWL.ENDPOINT')"
            :message="t('CORTEX_URLS.FIRECRAWL.ENDPOINT_HELP')"
          />
          <NextInput
            v-if="['cloud', 'custom'].includes(firecrawl.mode)"
            v-model="firecrawl.api_key"
            type="password"
            :label="t('CORTEX_URLS.FIRECRAWL.API_KEY')"
            :message="t('CORTEX_URLS.FIRECRAWL.API_KEY_HELP')"
          />
        </div>
        <div class="flex flex-wrap justify-end gap-2">
          <NextButton
            data-testid="test-firecrawl-connection"
            type="button"
            slate
            outline
            :label="t('CORTEX_MODELS.ACTIONS.TEST_CONNECTION')"
            icon="i-lucide-plug-zap"
            :is-loading="isTestingFirecrawl"
            :disabled="isTestingFirecrawl"
            class="!w-auto"
            @click="testFirecrawl"
          />
          <NextButton
            type="submit"
            :label="t('CORTEX_URLS.FIRECRAWL.SAVE')"
            icon="i-lucide-save"
            :is-loading="isSavingFirecrawl"
            class="!w-auto"
          />
        </div>
      </form>
      <template v-if="!firecrawlOnly">
        <form class="grid gap-4" @submit.prevent="savePolicy">
          <div>
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('CORTEX_URLS.POLICY.TITLE') }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('CORTEX_URLS.POLICY.DESCRIPTION') }}
            </p>
          </div>
          <div class="grid gap-4 md:grid-cols-2">
            <WithLabel
              name="cortex-url-mode"
              :label="t('CORTEX_URLS.POLICY.MODE')"
              :help-message="t('CORTEX_URLS.POLICY.HELP.MODE')"
            >
              <AdaptiveSelect
                v-model="policy.conversation_url_extraction_mode"
                :options="modeOptions"
              />
            </WithLabel>
            <WithLabel
              name="cortex-url-locale"
              :label="t('CORTEX_URLS.POLICY.LOCALE')"
              :help-message="t('CORTEX_URLS.POLICY.HELP.LOCALE')"
            >
              <AdaptiveSelect
                v-model="policy.canonical_locale"
                :options="localeOptions"
              />
            </WithLabel>
            <WithLabel
              name="cortex-url-message-limit"
              :label="t('CORTEX_URLS.POLICY.MESSAGE_LIMIT')"
              :help-message="t('CORTEX_URLS.POLICY.HELP.MESSAGE_LIMIT')"
            >
              <NextInput
                v-model="policy.max_links_per_message"
                type="number"
                min="1"
                max="20"
                class="w-full"
              />
            </WithLabel>
            <WithLabel
              name="cortex-url-conversation-limit"
              :label="t('CORTEX_URLS.POLICY.CONVERSATION_LIMIT')"
              :help-message="t('CORTEX_URLS.POLICY.HELP.CONVERSATION_LIMIT')"
            >
              <NextInput
                v-model="policy.max_links_per_conversation"
                type="number"
                min="1"
                max="200"
                class="w-full"
              />
            </WithLabel>
            <WithLabel
              name="cortex-url-period-limit"
              :label="t('CORTEX_URLS.POLICY.PERIOD_LIMIT')"
              :help-message="t('CORTEX_URLS.POLICY.HELP.PERIOD_LIMIT')"
            >
              <NextInput
                v-model="policy.max_links_per_period"
                type="number"
                min="1"
                max="10000"
                class="w-full"
              />
            </WithLabel>
            <WithLabel
              name="cortex-url-budget"
              :label="t('CORTEX_URLS.POLICY.BUDGET')"
              :help-message="t('CORTEX_URLS.POLICY.HELP.BUDGET')"
            >
              <NextInput
                v-model="policy.model_fallback_budget_usd"
                type="number"
                min="0"
                max="1000"
                step="0.01"
                class="w-full"
              />
            </WithLabel>
            <WithLabel
              name="cortex-url-concurrency"
              :label="t('CORTEX_URLS.POLICY.CONCURRENCY')"
              :help-message="t('CORTEX_URLS.POLICY.HELP.CONCURRENCY')"
            >
              <NextInput
                v-model="policy.settings.max_concurrent_ingestions"
                type="number"
                min="1"
                max="20"
                class="w-full"
              />
            </WithLabel>
          </div>
          <div class="grid gap-3 md:grid-cols-2">
            <label
              v-for="field in policyBooleanFields"
              :key="field.key"
              class="flex items-start gap-3 text-sm text-n-slate-12"
            >
              <NextCheckbox
                v-model="policy[field.key]"
                class="mt-0.5 shrink-0"
              />
              <span>
                <span class="block font-medium">{{ field.label }}</span>
                <span class="block text-n-slate-11">{{ field.help }}</span>
              </span>
            </label>
          </div>
          <div class="flex justify-end">
            <NextButton
              type="submit"
              blue
              icon="i-lucide-save"
              :label="t('CORTEX_URLS.POLICY.SAVE')"
              :is-loading="isSavingPolicy"
            />
          </div>
        </form>

        <section class="grid gap-4 border-t border-n-weak pt-5">
          <div>
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('CORTEX_URLS.DOMAINS.TITLE') }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('CORTEX_URLS.DOMAINS.DESCRIPTION') }}
            </p>
          </div>
          <form
            class="grid gap-3 md:grid-cols-[minmax(0,1fr)_12rem_auto]"
            @submit.prevent="addDomain"
          >
            <NextInput
              v-model="domainForm.domain"
              :placeholder="t('CORTEX_URLS.DOMAINS.PLACEHOLDER')"
              class="w-full"
              required
            />
            <AdaptiveSelect
              v-model="domainForm.status"
              :options="domainStatusOptions"
            />
            <NextButton
              type="submit"
              blue
              icon="i-lucide-plus"
              :label="t('CORTEX_URLS.DOMAINS.ADD')"
              :is-loading="isSavingDomain"
            />
          </form>
          <div class="overflow-x-auto rounded-lg border border-n-weak">
            <table class="min-w-full divide-y divide-n-weak text-sm">
              <thead class="bg-n-alpha-2 text-left text-xs text-n-slate-11">
                <tr>
                  <th class="px-3 py-2 font-medium">
                    {{ t('CORTEX_URLS.DOMAINS.DOMAIN') }}
                  </th>
                  <th class="px-3 py-2 font-medium">
                    {{ t('CORTEX_URLS.DOMAINS.STATE') }}
                  </th>
                  <th class="px-3 py-2 font-medium">
                    {{ t('CORTEX_URLS.DOMAINS.ACTIONS') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr
                  v-for="domainPolicy in domainPolicies"
                  :key="domainPolicy.id"
                >
                  <td class="px-3 py-2 font-medium text-n-slate-12">
                    {{ domainPolicy.domain }}
                  </td>
                  <td class="px-3 py-2">
                    <Label
                      compact
                      :label="domainStatusLabel(domainPolicy.status)"
                      :color="domainStatusColor(domainPolicy.status)"
                    />
                  </td>
                  <td class="px-3 py-2">
                    <div class="flex gap-1">
                      <NextButton
                        type="button"
                        xs
                        blue
                        outline
                        :label="t('CORTEX_URLS.DOMAINS.ALLOW')"
                        :disabled="domainPolicy.status === 'allowed'"
                        :is-loading="
                          activeDomainAction === `${domainPolicy.id}:allowed`
                        "
                        @click="updateDomain(domainPolicy, 'allowed')"
                      />
                      <NextButton
                        type="button"
                        xs
                        amber
                        outline
                        :label="t('CORTEX_URLS.DOMAINS.BLOCK')"
                        :disabled="domainPolicy.status === 'blocked'"
                        :is-loading="
                          activeDomainAction === `${domainPolicy.id}:blocked`
                        "
                        @click="updateDomain(domainPolicy, 'blocked')"
                      />
                      <NextButton
                        type="button"
                        xs
                        ruby
                        outline
                        icon="i-lucide-trash-2"
                        :label="t('CORTEX_URLS.DOMAINS.REMOVE')"
                        :is-loading="
                          activeDomainAction === `${domainPolicy.id}:delete`
                        "
                        @click="deleteDomain(domainPolicy)"
                      />
                    </div>
                  </td>
                </tr>
                <tr v-if="!domainPolicies.length">
                  <td colspan="3" class="px-3 py-4 text-center text-n-slate-11">
                    {{ t('CORTEX_URLS.DOMAINS.EMPTY') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="grid gap-4 border-t border-n-weak pt-5">
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ t('CORTEX_URLS.CANDIDATES.TITLE') }}
              </h3>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('CORTEX_URLS.CANDIDATES.DESCRIPTION') }}
              </p>
            </div>
            <NextButton
              type="button"
              sm
              slate
              outline
              icon="i-lucide-refresh-cw"
              :label="t('CORTEX_URLS.CANDIDATES.REFRESH')"
              @click="fetchData"
            />
          </div>
          <article
            v-for="candidate in reviewableCandidates"
            :key="candidate.id"
            class="grid gap-3 rounded-lg border border-n-weak p-3"
          >
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div class="min-w-0">
                <p class="truncate text-sm font-medium text-n-slate-12">
                  {{ candidate.redacted_url }}
                </p>
                <p class="mt-1 flex items-center gap-2 text-xs text-n-slate-11">
                  <span>{{ candidate.domain }}</span>
                  <span class="border-l border-n-weak pl-2">
                    {{ formatDate(candidate.created_at) }}
                  </span>
                </p>
              </div>
              <Label
                compact
                :label="candidateStatusLabel(candidate.status)"
                :color="candidateStatusColor(candidate.status)"
              />
            </div>
            <AdaptiveSelect
              v-model="candidateKnowledgeBases[candidate.id]"
              :options="knowledgeBaseOptions"
              :placeholder="t('CORTEX_URLS.CANDIDATES.SELECT_BASE')"
            />
            <div class="flex flex-wrap gap-2">
              <NextButton
                type="button"
                xs
                slate
                outline
                :label="t('CORTEX_URLS.CANDIDATES.IGNORE')"
                :is-loading="activeCandidateAction === `${candidate.id}:ignore`"
                @click="decideCandidate(candidate, 'ignore')"
              />
              <NextButton
                type="button"
                xs
                blue
                outline
                :label="t('CORTEX_URLS.CANDIDATES.ACCESS_ONCE')"
                :disabled="!candidateKnowledgeBases[candidate.id]"
                :is-loading="
                  activeCandidateAction === `${candidate.id}:access_once`
                "
                @click="decideCandidate(candidate, 'access_once')"
              />
              <NextButton
                type="button"
                xs
                teal
                outline
                :label="t('CORTEX_URLS.CANDIDATES.ADD_TO_KNOWLEDGE')"
                :disabled="!candidateKnowledgeBases[candidate.id]"
                :is-loading="
                  activeCandidateAction === `${candidate.id}:add_to_knowledge`
                "
                @click="decideCandidate(candidate, 'add_to_knowledge')"
              />
              <NextButton
                type="button"
                xs
                amber
                outline
                :label="t('CORTEX_URLS.CANDIDATES.AUTHORIZE_DOMAIN')"
                :disabled="!candidateKnowledgeBases[candidate.id]"
                :is-loading="
                  activeCandidateAction === `${candidate.id}:authorize_domain`
                "
                @click="decideCandidate(candidate, 'authorize_domain')"
              />
            </div>
          </article>
          <p
            v-if="!reviewableCandidates.length"
            class="text-sm text-n-slate-11"
          >
            {{ t('CORTEX_URLS.CANDIDATES.EMPTY') }}
          </p>
        </section>
      </template>
    </div>
  </SectionLayout>
</template>
